/**
 * Math Type Validators — clock, money, columnArithmetic, ratioTable,
 * numberSequence, gridShade, stepSolver, comparisonJudge
 *
 * Split from typeStage.ts (2786 LOC god-file) — P2-S2
 */

import type { ValidationIssue } from './shared'
import { STAGE, blocker, coach } from './shared'

// ─── Clock Validation ─────────────────────────────────────────────────────────

export function validateClock(
  data: Record<string, unknown>,
  qId: string,
  fId: string,
): ValidationIssue[] {
  const issues: ValidationIssue[] = []

  const validModes = [
    'read_analog_fill',
    'read_digital_fill',
    'analog_to_digital_mc',
    'digital_to_analog_mc',
  ]
  if (data.mode && !validModes.includes(data.mode as string)) {
    issues.push(
      blocker(
        'type',
        STAGE,
        'TYPE_CLOCK_INVALID_MODE',
        `Invalid clock mode: "${data.mode}"`,
        {
          field: 'data.mode',
          expected: validModes.join(' | '),
          actual: String(data.mode),
          questionId: qId,
          familyId: fId,
        },
      ),
    )
  }

  const target = data.target as Record<string, unknown> | undefined
  if (target && typeof target === 'object') {
    const hours = target.hours as number
    const minutes = target.minutes as number

    if (
      typeof hours !== 'number' ||
      hours < 0 ||
      hours > 23 ||
      !Number.isInteger(hours)
    ) {
      issues.push(
        blocker(
          'type',
          STAGE,
          'TYPE_CLOCK_INVALID_HOURS',
          `Clock hours must be integer 0-23, got: ${hours}`,
          {
            field: 'data.target.hours',
            expected: '0-23',
            actual: String(hours),
            questionId: qId,
            familyId: fId,
          },
        ),
      )
    }

    if (
      typeof minutes !== 'number' ||
      minutes < 0 ||
      minutes > 59 ||
      !Number.isInteger(minutes)
    ) {
      issues.push(
        blocker(
          'type',
          STAGE,
          'TYPE_CLOCK_INVALID_MINUTES',
          `Clock minutes must be integer 0-59, got: ${minutes}`,
          {
            field: 'data.target.minutes',
            expected: '0-59',
            actual: String(minutes),
            questionId: qId,
            familyId: fId,
          },
        ),
      )
    }
  }

  if (data.mode && String(data.mode).endsWith('_mc')) {
    if (
      !data.options ||
      !Array.isArray(data.options) ||
      (data.options as unknown[]).length < 2
    ) {
      issues.push(
        blocker(
          'type',
          STAGE,
          'TYPE_CLOCK_MC_MISSING_OPTIONS',
          'Clock MC mode requires options array with ≥2 entries',
          {
            field: 'data.options',
            questionId: qId,
            familyId: fId,
          },
        ),
      )
    }
  }

  return issues
}

// ─── Money Validation ─────────────────────────────────────────────────────────

export function validateMoney(
  data: Record<string, unknown>,
  qId: string,
  fId: string,
): ValidationIssue[] {
  const issues: ValidationIssue[] = []

  const validModes = ['make_amount', 'give_change']
  if (data.mode && !validModes.includes(data.mode as string)) {
    issues.push(
      blocker(
        'type',
        STAGE,
        'TYPE_MONEY_INVALID_MODE',
        `Invalid money mode: "${data.mode}"`,
        {
          field: 'data.mode',
          expected: validModes.join(' | '),
          actual: String(data.mode),
          questionId: qId,
          familyId: fId,
        },
      ),
    )
  }

  const targetCents = data.targetCents as number
  if (typeof targetCents === 'number') {
    if (targetCents <= 0 || !Number.isInteger(targetCents)) {
      issues.push(
        blocker(
          'type',
          STAGE,
          'TYPE_MONEY_INVALID_TARGET',
          `targetCents must be positive integer, got: ${targetCents}`,
          {
            field: 'data.targetCents',
            expected: 'positive integer',
            actual: String(targetCents),
            questionId: qId,
            familyId: fId,
          },
        ),
      )
    }
  }

  const denoms = data.allowedDenoms
  if (denoms && Array.isArray(denoms)) {
    for (let i = 0; i < (denoms as unknown[]).length; i++) {
      const d = (denoms as Record<string, unknown>[])[i]
      if (!d || typeof d !== 'object') {
        issues.push(
          blocker(
            'type',
            STAGE,
            'TYPE_MONEY_INVALID_DENOM',
            `allowedDenoms[${i}] must be an object`,
            {
              field: `data.allowedDenoms[${i}]`,
              questionId: qId,
              familyId: fId,
            },
          ),
        )
        continue
      }
      if (typeof d.valueCents !== 'number' || (d.valueCents as number) <= 0) {
        issues.push(
          blocker(
            'type',
            STAGE,
            'TYPE_MONEY_INVALID_DENOM_VALUE',
            `allowedDenoms[${i}].valueCents must be positive number`,
            {
              field: `data.allowedDenoms[${i}].valueCents`,
              questionId: qId,
              familyId: fId,
            },
          ),
        )
      }
      if (d.type !== 'coin' && d.type !== 'bill') {
        issues.push(
          coach(
            'type',
            STAGE,
            'TYPE_MONEY_INVALID_DENOM_TYPE',
            `allowedDenoms[${i}].type should be "coin" or "bill"`,
            {
              field: `data.allowedDenoms[${i}].type`,
              expected: 'coin | bill',
              actual: String(d.type),
              questionId: qId,
              familyId: fId,
              severity: 40,
            },
          ),
        )
      }
    }
  } else if (denoms !== undefined) {
    issues.push(
      blocker(
        'type',
        STAGE,
        'TYPE_MONEY_DENOMS_NOT_ARRAY',
        'allowedDenoms must be an array',
        {
          field: 'data.allowedDenoms',
          questionId: qId,
          familyId: fId,
        },
      ),
    )
  }

  if (data.mode === 'give_change') {
    if (
      typeof data.priceCents !== 'number' ||
      (data.priceCents as number) <= 0
    ) {
      issues.push(
        blocker(
          'type',
          STAGE,
          'TYPE_MONEY_MISSING_PRICE',
          'give_change mode requires positive priceCents',
          {
            field: 'data.priceCents',
            questionId: qId,
            familyId: fId,
          },
        ),
      )
    }
    if (typeof data.paidCents !== 'number' || (data.paidCents as number) <= 0) {
      issues.push(
        blocker(
          'type',
          STAGE,
          'TYPE_MONEY_MISSING_PAID',
          'give_change mode requires positive paidCents',
          {
            field: 'data.paidCents',
            questionId: qId,
            familyId: fId,
          },
        ),
      )
    }
    if (
      typeof data.paidCents === 'number' &&
      typeof data.priceCents === 'number' &&
      (data.paidCents as number) < (data.priceCents as number)
    ) {
      issues.push(
        blocker(
          'type',
          STAGE,
          'TYPE_MONEY_PAID_LESS_THAN_PRICE',
          'paidCents must be ≥ priceCents for give_change',
          {
            field: 'data.paidCents',
            expected: `≥ ${data.priceCents}`,
            actual: String(data.paidCents),
            questionId: qId,
            familyId: fId,
          },
        ),
      )
    }
  }

  return issues
}

// ─── Column Arithmetic Validation ─────────────────────────────────────────────

export function validateColumnArithmetic(
  data: Record<string, unknown>,
  type: string,
  qId: string,
  fId: string,
): ValidationIssue[] {
  const issues: ValidationIssue[] = []

  const op1 = data.operand1 as number | undefined
  const op2 = data.operand2 as number | undefined

  if (typeof op1 === 'number' && typeof op2 === 'number') {
    if (op1 <= 0 || op2 <= 0) {
      issues.push(
        blocker(
          'type',
          STAGE,
          'TYPE_COLUMN_NEGATIVE_OPERAND',
          'Column arithmetic operands must be positive',
          {
            field: 'data.operand1',
            questionId: qId,
            familyId: fId,
          },
        ),
      )
    }

    if (type === 'cijferend_aftrekken' && op1 < op2) {
      issues.push(
        blocker(
          'type',
          STAGE,
          'TYPE_COLUMN_SUB_NEGATIVE_RESULT',
          'For subtraction, operand1 must be ≥ operand2',
          {
            field: 'data.operand1',
            expected: `≥ ${op2}`,
            actual: String(op1),
            questionId: qId,
            familyId: fId,
          },
        ),
      )
    }

    if (!Number.isInteger(op1) || !Number.isInteger(op2)) {
      issues.push(
        coach(
          'type',
          STAGE,
          'TYPE_COLUMN_NON_INTEGER',
          'Column arithmetic typically uses integers',
          {
            field: 'data.operand1',
            questionId: qId,
            familyId: fId,
            severity: 35,
          },
        ),
      )
    }
  }

  return issues
}

// ─── Ratio Table Validation ───────────────────────────────────────────────────

export function validateRatioTable(
  data: Record<string, unknown>,
  qId: string,
  fId: string,
): ValidationIssue[] {
  const issues: ValidationIssue[] = []

  const headers = data.headers as unknown[] | undefined
  const rows = data.rows as unknown[] | undefined
  const hiddenCells = data.hiddenCells as unknown[] | undefined

  if (headers && Array.isArray(headers)) {
    if (headers.length < 2) {
      issues.push(
        coach(
          'type',
          STAGE,
          'TYPE_RATIO_TABLE_HEADERS',
          'Ratio table should have at least 2 headers',
          {
            field: 'data.headers',
            expected: '≥2',
            actual: String(headers.length),
            questionId: qId,
            familyId: fId,
            severity: 45,
          },
        ),
      )
    }
  }

  if (rows && Array.isArray(rows)) {
    for (let i = 0; i < rows.length; i++) {
      const row = rows[i]
      const expectedCols =
        headers && Array.isArray(headers) ? headers.length : 2
      if (!Array.isArray(row) || (row as unknown[]).length !== expectedCols) {
        issues.push(
          coach(
            'type',
            STAGE,
            'TYPE_RATIO_TABLE_ROW_FORMAT',
            `rows[${i}] should have ${expectedCols} columns (matching headers)`,
            {
              field: `data.rows[${i}]`,
              questionId: qId,
              familyId: fId,
              severity: 50,
            },
          ),
        )
      }
    }
  }

  if (!hiddenCells || !Array.isArray(hiddenCells)) {
    issues.push(
      blocker(
        'type',
        STAGE,
        'TYPE_RATIO_TABLE_NO_HIDDEN_CELLS',
        'Ratio table requires data.hiddenCells array',
        {
          field: 'data.hiddenCells',
          questionId: qId,
          familyId: fId,
          suggestion: 'Add data.hiddenCells = [{row: 0, col: 1}, ...]',
        },
      ),
    )
  } else {
    if (hiddenCells.length === 0) {
      issues.push(
        blocker(
          'type',
          STAGE,
          'TYPE_RATIO_TABLE_EMPTY_HIDDEN_CELLS',
          'Ratio table must have at least one hidden cell',
          {
            field: 'data.hiddenCells',
            questionId: qId,
            familyId: fId,
          },
        ),
      )
    }

    for (let i = 0; i < hiddenCells.length; i++) {
      const cell = hiddenCells[i] as Record<string, unknown> | undefined
      if (!cell || typeof cell !== 'object') {
        issues.push(
          blocker(
            'type',
            STAGE,
            'TYPE_RATIO_TABLE_INVALID_HIDDEN_CELL',
            `hiddenCells[${i}] must be an object with row and col`,
            {
              field: `data.hiddenCells[${i}]`,
              questionId: qId,
              familyId: fId,
            },
          ),
        )
      } else {
        if (typeof cell.row !== 'number' || typeof cell.col !== 'number') {
          issues.push(
            blocker(
              'type',
              STAGE,
              'TYPE_RATIO_TABLE_INVALID_CELL_COORDS',
              `hiddenCells[${i}] must have numeric row and col`,
              {
                field: `data.hiddenCells[${i}]`,
                questionId: qId,
                familyId: fId,
              },
            ),
          )
        }

        if (rows && Array.isArray(rows)) {
          const rowIdx = cell.row as number
          const colIdx = cell.col as number
          if (rowIdx < 0 || rowIdx >= rows.length) {
            issues.push(
              blocker(
                'type',
                STAGE,
                'TYPE_RATIO_TABLE_ROW_OUT_OF_BOUNDS',
                `hiddenCells[${i}].row (${rowIdx}) out of range [0, ${rows.length - 1}]`,
                {
                  field: `data.hiddenCells[${i}].row`,
                  questionId: qId,
                  familyId: fId,
                },
              ),
            )
          }
          const maxCol =
            headers && Array.isArray(headers) ? headers.length - 1 : 1
          if (colIdx < 0 || colIdx > maxCol) {
            issues.push(
              blocker(
                'type',
                STAGE,
                'TYPE_RATIO_TABLE_COL_OUT_OF_BOUNDS',
                `hiddenCells[${i}].col (${colIdx}) must be 0-${maxCol}`,
                {
                  field: `data.hiddenCells[${i}].col`,
                  questionId: qId,
                  familyId: fId,
                },
              ),
            )
          }
        }
      }
    }
  }

  return issues
}

// ─── Number Sequence Validation ───────────────────────────────────────────────

export function validateNumberSequence(
  data: Record<string, unknown>,
  qId: string,
  fId: string,
): ValidationIssue[] {
  const issues: ValidationIssue[] = []

  const sequence = data.sequence as unknown[] | undefined
  const missingIndex = data.missingIndex as number | undefined

  if (sequence && Array.isArray(sequence)) {
    if (sequence.length < 3) {
      issues.push(
        coach(
          'type',
          STAGE,
          'TYPE_NUMBER_SEQ_TOO_SHORT',
          'Number sequence should have at least 3 elements',
          {
            field: 'data.sequence',
            questionId: qId,
            familyId: fId,
            severity: 40,
          },
        ),
      )
    }

    if (typeof missingIndex === 'number') {
      if (missingIndex < 0 || missingIndex >= sequence.length) {
        issues.push(
          blocker(
            'type',
            STAGE,
            'TYPE_NUMBER_SEQ_INVALID_INDEX',
            `missingIndex ${missingIndex} out of range [0, ${sequence.length - 1}]`,
            {
              field: 'data.missingIndex',
              expected: `0-${sequence.length - 1}`,
              actual: String(missingIndex),
              questionId: qId,
              familyId: fId,
            },
          ),
        )
      }
    }
  }

  if (data.correctValue === undefined || data.correctValue === null) {
    issues.push(
      blocker(
        'type',
        STAGE,
        'TYPE_NUMBER_SEQ_NO_ANSWER',
        'Number sequence requires correctValue',
        {
          field: 'data.correctValue',
          questionId: qId,
          familyId: fId,
        },
      ),
    )
  }

  return issues
}

// ─── Grid Shade Validation ────────────────────────────────────────────────────

export function validateGridShade(
  data: Record<string, unknown>,
  qId: string,
  fId: string,
): ValidationIssue[] {
  const issues: ValidationIssue[] = []

  const gridRows = data.gridRows as number | undefined
  const gridCols = data.gridCols as number | undefined
  const targetCount = data.targetCount as number | undefined
  const mode = data.mode as string | undefined

  if (
    typeof gridRows !== 'number' ||
    gridRows < 1 ||
    !Number.isInteger(gridRows)
  ) {
    issues.push(
      blocker(
        'type',
        STAGE,
        'TYPE_GRID_SHADE_INVALID_DIMS',
        'grid_shade requires data.gridRows as positive integer',
        {
          field: 'data.gridRows',
          questionId: qId,
          familyId: fId,
        },
      ),
    )
  }

  if (
    typeof gridCols !== 'number' ||
    gridCols < 1 ||
    !Number.isInteger(gridCols)
  ) {
    issues.push(
      blocker(
        'type',
        STAGE,
        'TYPE_GRID_SHADE_INVALID_DIMS',
        'grid_shade requires data.gridCols as positive integer',
        {
          field: 'data.gridCols',
          questionId: qId,
          familyId: fId,
        },
      ),
    )
  }

  if (
    typeof targetCount === 'number' &&
    typeof gridRows === 'number' &&
    typeof gridCols === 'number'
  ) {
    if (targetCount < 1 || targetCount > gridRows * gridCols) {
      issues.push(
        blocker(
          'type',
          STAGE,
          'TYPE_GRID_SHADE_INVALID_TARGET',
          `targetCount (${targetCount}) must be between 1 and ${gridRows * gridCols}`,
          {
            field: 'data.targetCount',
            expected: `1-${gridRows * gridCols}`,
            actual: String(targetCount),
            questionId: qId,
            familyId: fId,
          },
        ),
      )
    }
  }

  const validModes = ['shade_count', 'shade_fraction', 'shade_to_match']
  if (mode && !validModes.includes(mode)) {
    issues.push(
      blocker(
        'type',
        STAGE,
        'TYPE_GRID_SHADE_INVALID_MODE',
        `Invalid grid_shade mode: "${mode}"`,
        {
          field: 'data.mode',
          expected: validModes.join(' | '),
          actual: mode,
          questionId: qId,
          familyId: fId,
        },
      ),
    )
  }

  return issues
}

// ─── Step Solver Validation ───────────────────────────────────────────────────

export function validateStepSolver(
  data: Record<string, unknown>,
  qId: string,
  fId: string,
): ValidationIssue[] {
  const issues: ValidationIssue[] = []

  const steps = data.steps as unknown[] | undefined

  if (!steps || !Array.isArray(steps)) {
    issues.push(
      blocker(
        'type',
        STAGE,
        'TYPE_STEP_SOLVER_NO_STEPS',
        'step_solver requires data.steps array',
        {
          field: 'data.steps',
          questionId: qId,
          familyId: fId,
        },
      ),
    )
    return issues
  }

  if (steps.length < 1) {
    issues.push(
      blocker(
        'type',
        STAGE,
        'TYPE_STEP_SOLVER_NO_STEPS',
        'step_solver must have at least 1 step',
        {
          field: 'data.steps',
          questionId: qId,
          familyId: fId,
        },
      ),
    )
  }

  for (let i = 0; i < steps.length; i++) {
    const step = steps[i] as Record<string, unknown> | undefined
    if (!step || typeof step !== 'object') {
      issues.push(
        blocker(
          'type',
          STAGE,
          'TYPE_STEP_SOLVER_INVALID_STEP',
          `steps[${i}] must be an object with { prompt, correctAnswer }`,
          {
            field: `data.steps[${i}]`,
            questionId: qId,
            familyId: fId,
          },
        ),
      )
    } else {
      if (
        typeof step.prompt !== 'string' ||
        (step.prompt as string).trim() === ''
      ) {
        issues.push(
          blocker(
            'type',
            STAGE,
            'TYPE_STEP_SOLVER_INVALID_STEP',
            `steps[${i}].prompt must be a non-empty string`,
            {
              field: `data.steps[${i}].prompt`,
              questionId: qId,
              familyId: fId,
            },
          ),
        )
      }
      if (step.correctAnswer === undefined || step.correctAnswer === null) {
        issues.push(
          blocker(
            'type',
            STAGE,
            'TYPE_STEP_SOLVER_INVALID_STEP',
            `steps[${i}].correctAnswer is required`,
            {
              field: `data.steps[${i}].correctAnswer`,
              questionId: qId,
              familyId: fId,
            },
          ),
        )
      }
    }
  }

  return issues
}

// ─── Comparison Judge Validation ──────────────────────────────────────────────

export function validateComparisonJudge(
  data: Record<string, unknown>,
  qId: string,
  fId: string,
): ValidationIssue[] {
  const issues: ValidationIssue[] = []

  const items = data.items as unknown[] | undefined
  const criterion = data.criterion as string | undefined
  const correctIndex = data.correctIndex as number | undefined

  if (!items || !Array.isArray(items)) {
    issues.push(
      blocker(
        'type',
        STAGE,
        'TYPE_COMPARISON_TOO_FEW_ITEMS',
        'comparison_judge requires data.items array',
        {
          field: 'data.items',
          questionId: qId,
          familyId: fId,
        },
      ),
    )
    return issues
  }

  if (items.length < 2) {
    issues.push(
      coach(
        'type',
        STAGE,
        'TYPE_COMPARISON_TOO_FEW_ITEMS',
        'comparison_judge should have at least 2 items',
        {
          field: 'data.items',
          questionId: qId,
          familyId: fId,
          severity: 45,
        },
      ),
    )
  }

  if (typeof criterion !== 'string' || criterion.trim() === '') {
    issues.push(
      blocker(
        'type',
        STAGE,
        'TYPE_COMPARISON_NO_CRITERION',
        'comparison_judge requires non-empty data.criterion',
        {
          field: 'data.criterion',
          questionId: qId,
          familyId: fId,
        },
      ),
    )
  }

  if (
    typeof correctIndex !== 'number' ||
    correctIndex < 0 ||
    correctIndex >= items.length ||
    !Number.isInteger(correctIndex)
  ) {
    issues.push(
      blocker(
        'type',
        STAGE,
        'TYPE_COMPARISON_INVALID_INDEX',
        `correctIndex must be a valid index into items [0, ${items.length - 1}]`,
        {
          field: 'data.correctIndex',
          expected: `0-${items.length - 1}`,
          actual: String(correctIndex),
          questionId: qId,
          familyId: fId,
        },
      ),
    )
  }

  return issues
}
